/**
 * DonationCaptureAmountStep.tsx - Donation Amount Selection Step
 *
 * This component handles the first step of donation capture where users
 * select their donation amount from preset options or enter a custom amount.
 *
 * Key Features:
 * - Preset donation amount buttons
 * - Custom amount input with validation
 * - Donation frequency selection (one-time vs recurring)
 * - Amount formatting and validation
 * - Minimum and maximum amount enforcement
 * - Responsive grid layout for amount options
 *
 * Amount Options:
 * - Preset amounts based on fundraiser configuration
 * - Custom amount input with currency formatting
 * - Minimum donation amount validation
 * - Maximum donation amount limits (if configured)
 *
 * Props:
 * - selectedAmount: number - Currently selected donation amount
 * - onAmountChange: (amount: number) => void - Amount change handler
 * - onNext: () => void - Proceed to next step handler
 * - isRecurring?: boolean - Whether donation is recurring
 * - onRecurringChange?: (recurring: boolean) => void - Recurring change handler
 *
 * Dependencies: MobX store, amount validation, currency formatting
 */

import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { fundraiserStore } from "../../stores/FundraiserStore";
import { DonationAmountSelection } from "../../enums";
import { DonationPricingOption } from "@mindmemobile/mm-payment-modules";
import UpArrow from "../../../../../images/common/up-arrow.svg";
import { UtilityMethods } from "@mindmemobile/mm-shared-global";
import { parseCurrencyAmount, sanitizeCurrencyInput } from "../../utils/formatters";

const AmountGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 24px;
`;

const AmountButton = styled.button<{ selected: boolean }>`
  padding: 14px 16px;
  border: 2px solid ${(props) => (props.selected ? "#10b981" : "#e2e8f0")};
  background: ${(props) => (props.selected ? "#10b981" : "#ffffff")};
  color: ${(props) => (props.selected ? "#ffffff" : "#1e293b")};
  border-radius: 12px;
  font-size: 20px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${(props) =>
      props.selected
        ? "0 8px 24px rgba(16, 185, 129, 0.4)"
        : "0 4px 12px rgba(0, 0, 0, 0.1)"};
    border-color: #10b981;
    background: ${(props) => (props.selected ? "#10b981" : "#f0fdf4")};
  }

  &:active {
    transform: translateY(0);
  }
`;

const CustomAmountContainer = styled.div`
  position: relative;
  margin-bottom: 24px;
`;

const CustomAmountInput = styled.input<{ hasError: boolean }>`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid ${(props) => (props.hasError ? "#dc2626" : "#e2e8f0")};
  border-radius: 12px;
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  transition: border-color 0.2s ease;
  text-align: center;
  position: relative;

  /* Hide number input spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type="number"] {
    -moz-appearance: textfield;
  }

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#dc2626" : "#10b981")};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.hasError ? "rgba(220, 38, 38, 0.1)" : "rgba(16, 185, 129, 0.1)"};
  }

  &:hover {
    border-color: ${(props) => (props.hasError ? "#dc2626" : "#10b981")};
    background: ${(props) => (props.hasError ? "#fef2f2" : "#f0fdf4")};
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 500;
  }
`;

const AmountErrorText = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  font-weight: 600;
  color: #dc2626;
  pointer-events: none;
  white-space: nowrap;
`;

const FrequencyContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
`;

const FrequencyButton = styled.button<{ selected: boolean }>`
  flex: 1;
  padding: 16px 24px;
  border: 2px solid ${(props) => (props.selected ? "#10b981" : "#e2e8f0")};
  background: ${(props) => (props.selected ? "#10b981" : "#ffffff")};
  color: ${(props) => (props.selected ? "#ffffff" : "#64748b")};
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    border-color: #10b981;
    background: ${(props) => (props.selected ? "#059669" : "#f0fdf4")};
  }
`;

const MonthlyNote = styled.div`
  display: flex;
  align-items: flex-start;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const MonthlyIcon = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const UpArrowIcon = styled.img`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
`;

const MonthlyText = styled.div`
  color: #64748b;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.4;
  white-space: pre-line;
`;

const CommentSection = styled.div`
  margin-bottom: 32px;
`;

const CommentContainer = styled.div`
  position: relative;
`;

const CommentTextArea = styled.textarea`
  width: 100%;
  padding: 16px 16px 50px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 18px;
  color: #1e293b;
  resize: vertical;
  min-height: 110px;
  font-family: inherit;
  transition: border-color 0.2s ease;
  overflow-y: hidden;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const EmojiFooter = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  gap: 4px;
  pointer-events: none;
`;

const EmojiOptions = styled.div<{ visible: boolean }>`
  display: flex;
  gap: 4px;
  margin-right: 8px;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transform: translateX(${(props) => (props.visible ? "0" : "20px")});
  transition: all 0.3s ease;
  animation: ${(props) =>
    props.visible ? "slideInLeft 0.3s ease-out" : "none"};
  pointer-events: auto;

  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const EmojiOption = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
  pointer-events: auto;

  &:hover {
    background: #f1f5f9;
    transform: scale(1.32);
  }
`;

const EmojiButton = styled.button`
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
  filter: hue-rotate(15deg) saturate(1.2) brightness(1.1);
  pointer-events: auto;

  &:hover {
    background: #f1f5f9;
    transform: scale(1.2);
  }
`;

interface AmountData {
  selectedAmount: number | null;
  customAmount: string;
  frequency: "one-time" | "monthly";
  comment: string;
}

interface DonationCaptureAmountStepProps {
  initialData: AmountData;
  onDataChange: (data: AmountData) => void;
  onValidationChange: (isValid: boolean) => void;
}

const additionalEmojis = [
  "❤️",
  "🙏",
  "😢",
  "💕",
  "👏",
  "🎉",
  "😇",
  "💪",
  "⭐",
  "🌎",
  "🐶",
  "🫶",
  "👍",
  "🐱",
  "🌱",
];

export const DonationCaptureAmountStep: React.FC<
  DonationCaptureAmountStepProps
> = ({ initialData, onDataChange, onValidationChange }) => {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(
    initialData.selectedAmount,
  );
  const [customAmount, setCustomAmount] = useState(initialData.customAmount);
  const [frequency, setFrequency] = useState<"one-time" | "monthly">(
    initialData.frequency,
  );
  const [comment, setComment] = useState(initialData.comment);
  const [showAdditionalEmojis, setShowAdditionalEmojis] = useState(false);
  const [isCustomAmountFocused, setIsCustomAmountFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  function extractNumbers(input: string): number[] {
    // Match numbers that may have thousands separators (e.g., 1,000)
    const matches = input.match(/\b\d{1,3}(?:,\d{3})*\b/g) || [];

    const numbers = matches.map((s) => Number(s.replace(/,/g, "")));
    return numbers;
  }

  // Get donation amounts based on builder settings
  const getDonationAmounts = () => {
    const { builderData } = fundraiserStore;
    const donationAmountsType =
      builderData?.donationAmountsType || DonationAmountSelection.Default;

    const defaultAmounts = [25, 50, 100, 150, 200, 250];
    if (
      donationAmountsType === DonationAmountSelection.Custom &&
      builderData?.customDonationAmounts
    ) {
      return builderData.customDonationAmounts;
    } else if (
      donationAmountsType === DonationAmountSelection.FromItem &&
      builderData?.donationCustomerFacingCheckOutDetails
        ?.donationPricingPageData
    ) {
      switch (
        DonationPricingOption[
          builderData.donationCustomerFacingCheckOutDetails
            .donationPricingPageData.pricingOption
        ]
      ) {
        case DonationPricingOption.Option1:
          return extractNumbers(DonationPricingOption.Option1);
          break;
        case DonationPricingOption.Option2:
          return extractNumbers(DonationPricingOption.Option2);
          break;
        case DonationPricingOption.Option3:
          return extractNumbers(DonationPricingOption.Option3);
        case DonationPricingOption.Option4:
          return extractNumbers(DonationPricingOption.Option4);
        case DonationPricingOption.Option5:
          return extractNumbers(DonationPricingOption.Option5);
          break;
        case DonationPricingOption.CustomAll:
          return builderData.customDonationAmounts || defaultAmounts;
          break;
      }

      // Default amounts
      return defaultAmounts;
    }
    return defaultAmounts;
  };
  // Check if custom donations are allowed
  const isCustomDonationAllowed = () => {
    const { builderData } = fundraiserStore;
    const donationAmountsType =
      builderData?.donationAmountsType || DonationAmountSelection.Default;

    if (donationAmountsType === DonationAmountSelection.FromItem) {
      return true; // Always allow custom for "From Item"
    }

    return builderData?.allowCustomDonations !== false;
  };

  // Check if monthly donations are allowed
  const isMonthlyDonationAllowed = () => {
    const { builderData } = fundraiserStore;
    const donationAmountsType =
      builderData?.donationAmountsType || DonationAmountSelection.Default;

    if (donationAmountsType === DonationAmountSelection.FromItem) {
      return true; // Always allow monthly for "From Item"
    }

    return builderData?.allowMonthlyDonations !== false;
  };

  const presetAmounts = getDonationAmounts();

  /* The donation limits come only from the fundraiser being shown (fundraiserStore.builderData). They used
     to be overwritten here from a browser-wide localStorage key the builder writes, so the min/max of the
     last fundraiser edited in this browser could replace this campaign's real limits (security finding F05). */

  // Ensure frequency is set to 'one-time' if monthly donations are not allowed
  useEffect(() => {
    if (!isMonthlyDonationAllowed() && frequency === "monthly") {
      setFrequency("one-time");
    }
  }, [frequency]);

  // Update parent when data changes
  useEffect(() => {
    const data: AmountData = {
      selectedAmount,
      customAmount,
      frequency,
      comment,
    };
    onDataChange(data);
  }, [selectedAmount, customAmount, frequency, comment, onDataChange]);

  // Update validation when relevant data changes
  useEffect(() => {
    const isValid = isStepValid();
    onValidationChange(isValid);
  }, [selectedAmount, customAmount, onValidationChange]);

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount("");
  };

  const handleCustomAmountChange = (value: string) => {
    // Dollars and cents (F04): "10.50" stays 10.50 instead of becoming 1050.
    const cleanedValue = sanitizeCurrencyInput(value);
    setCustomAmount(cleanedValue === "0" ? "" : cleanedValue);
    setSelectedAmount(null);
  };

  const autoResizeTextarea = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = "auto";
    textarea.style.height = Math.max(110, textarea.scrollHeight) + "px";
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);
    autoResizeTextarea(e.target);
    // NOTE: do NOT call scrollIntoView() here. It used to run on every
    // keystroke with { behavior: "smooth", block: "start" }, which forced the
    // page to scroll the textarea to the top of the viewport on each character.
    // On the public fundraiser page the donation box sits near the bottom, so
    // this yanked the page down and the queued smooth-scroll animations fought
    // each other, making the field effectively untypable. Browsers already keep
    // the caret of a focused textarea in view as it grows.
  };

  const handleEmojiClick = (emoji: string) => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newComment =
        comment.substring(0, start) + emoji + comment.substring(end);
      setComment(newComment);

      // Set cursor position after the emoji
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
        textarea.focus();
        autoResizeTextarea(textarea);
      }, 0);
    } else {
      setComment((prev) => prev + emoji);
    }
  };

  const handlePrimaryEmojiClick = () => {
    if (!showAdditionalEmojis) {
      setShowAdditionalEmojis(true);
    } else {
      handleEmojiClick("😊");
    }
  };

  const handleCustomAmountFocus = () => {
    setIsCustomAmountFocused(true);
  };

  const handleCustomAmountBlur = () => {
    setIsCustomAmountFocused(false);
  };

  const handleFrequencyChange = (newFrequency: "one-time" | "monthly") => {
    setFrequency(newFrequency);
  };

  const formatCustomAmountDisplay = () => {
    if (isCustomAmountFocused || !customAmount) {
      return customAmount;
    }
    const amount = parseCurrencyAmount(customAmount);
    return amount === 0
      ? customAmount
      : `$${amount.toLocaleString(undefined, { minimumFractionDigits: amount % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;
  };

  const getCustomAmountValue = () => {
    if (isCustomAmountFocused) {
      return customAmount;
    }
    return formatCustomAmountDisplay();
  };

  const getCurrentAmount = () => {
    if (customAmount) {
      return parseCurrencyAmount(customAmount);
    }
    return selectedAmount || 0;
  };

  const validateCustomAmount = () => {
    if (!customAmount) return { isValid: true, errorMessage: "" };

    const amount = parseFloat(customAmount);
    if (isNaN(amount)) return { isValid: true, errorMessage: "" };

    const { builderData } = fundraiserStore;
    const restrictionsEnabled = builderData?.donationRestrictionsEnabled;

    if (!restrictionsEnabled) return { isValid: true, errorMessage: "" };

    const minAmount = Number(
      builderData?.minDonationAmount?.replace(/,/g, "") || "0",
    );
    const maxAmount = Number(
      builderData?.maxDonationAmount?.replace(/,/g, "") || "0",
    );

    if (minAmount > 0 && amount < minAmount) {
      return {
        isValid: false,
        errorMessage: `$${minAmount.toLocaleString()} minimum`,
      };
    }

    if (maxAmount > 0 && amount > maxAmount) {
      return {
        isValid: false,
        errorMessage: `$${maxAmount.toLocaleString()} maximum`,
      };
    }

    return { isValid: true, errorMessage: "" };
  };

  const isStepValid = () => {
    const amount = getCurrentAmount();
    const validation = validateCustomAmount();
    return amount > 0 && validation.isValid;
  };

  const handleEmojiButtonClick = () => {
    if (showAdditionalEmojis) {
      // If emojis are already showing, insert the smile emoji
      setComment((prev) => prev + "😊");
    } else {
      // If emojis are not showing, just show them
      setShowAdditionalEmojis(true);
    }
  };

  const amountValidation = validateCustomAmount();

  return (
    <>
      <AmountGrid>
        {presetAmounts
          .filter((amt) => amt > 0)
          .map((amount) => (
            <AmountButton
              key={amount}
              selected={selectedAmount === amount}
              onClick={() => handleAmountSelect(amount)}
            >
              ${UtilityMethods.numberWithCommas(amount)}
            </AmountButton>
          ))}
      </AmountGrid>

      {isCustomDonationAllowed() && (
        <CustomAmountContainer>
          <CustomAmountInput
            type="text"
            inputMode="decimal"
            placeholder="Enter custom amount"
            value={getCustomAmountValue()}
            onChange={(e) => handleCustomAmountChange(e.target.value)}
            onFocus={handleCustomAmountFocus}
            onBlur={handleCustomAmountBlur}
            hasError={!amountValidation.isValid}
          />
          {!amountValidation.isValid && amountValidation.errorMessage && (
            <AmountErrorText>{amountValidation.errorMessage}</AmountErrorText>
          )}
        </CustomAmountContainer>
      )}

      {isMonthlyDonationAllowed() && (
        <>
          <FrequencyContainer>
            <FrequencyButton
              selected={frequency === "one-time"}
              onClick={() => handleFrequencyChange("one-time")}
            >
              One Time
            </FrequencyButton>
            <FrequencyButton
              selected={frequency === "monthly"}
              onClick={() => handleFrequencyChange("monthly")}
            >
              Monthly
            </FrequencyButton>
          </FrequencyContainer>

          <MonthlyNote>
            <MonthlyIcon>
              <UpArrowIcon src={UpArrow} alt=" " />
            </MonthlyIcon>
            <MonthlyText>
              {frequency === "one-time"
                ? "Make a greater impact with a monthly donation! You can cancel at any time."
                : "Thank you for your monthly donation! It will make a huge difference.\nYou can cancel at any time."}
            </MonthlyText>
          </MonthlyNote>
        </>
      )}

      <CommentSection>
        <CommentContainer>
          <CommentTextArea
            ref={textareaRef}
            placeholder="Comment (optional)"
            value={comment}
            onChange={handleCommentChange}
          />
          <EmojiFooter>
            <EmojiOptions visible={showAdditionalEmojis}>
              {additionalEmojis.map((emoji, index) => (
                <EmojiOption
                  key={index}
                  type="button"
                  onClick={() => handleEmojiClick(emoji)}
                >
                  {emoji}
                </EmojiOption>
              ))}
            </EmojiOptions>
            <EmojiButton type="button" onClick={handlePrimaryEmojiClick}>
              😊
            </EmojiButton>
          </EmojiFooter>
        </CommentContainer>
      </CommentSection>
    </>
  );
};
